import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Modal,
  PageHeader,
  Pill,
} from "@/components/ui";
import { ArchiveBanner } from "@/components/ArchiveBanner";

interface ClusterServerSummary {
  id: number;
  name: string;
  host: string;
  port: number;
  is_active: boolean;
  connections: number | null;
  reachable: boolean | null;
  active: boolean | null;
}

interface Cluster {
  id: number;
  name: string;
  domain: string;
  public_secret: string;
  ad_tag: string | null;
  port: number;
  is_active: boolean;
  created_at: string;
  servers: ClusterServerSummary[];
  total_connections: number;
  link: string;
}

interface ClustersResponse {
  clusters: Cluster[];
}

export default function Clusters() {
  const [createOpen, setCreateOpen] = useState(false);
  const qc = useQueryClient();

  const q = useQuery<ClustersResponse>({
    queryKey: ["clusters"],
    queryFn: () => api("/api/admin/clusters"),
    refetchInterval: 30000,
  });

  const clusters = q.data?.clusters ?? [];

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Кластеры"
        right={
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <div className="text-zinc-400 text-sm">всего: {clusters.length}</div>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              + Создать
            </Button>
          </div>
        }
      />
      <ArchiveBanner what="Кластеры старой модели (ProxyCluster)" />

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}

      {!q.isLoading && clusters.length === 0 && (
        <div className="text-zinc-500">
          Кластеров пока нет. Кластер нужен когда у тебя несколько ad-серверов за одним
          доменом — один публичный secret, DNS round-robin, добавил сервер → вписал в
          кластер → DNS-запись → готово.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {clusters.map((c) => (
          <ClusterCard key={c.id} cluster={c} />
        ))}
      </div>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Новый кластер"
      >
        <ClusterCreateForm
          onCreated={() => {
            setCreateOpen(false);
            qc.invalidateQueries({ queryKey: ["clusters"] });
          }}
          onCancel={() => setCreateOpen(false)}
        />
      </Modal>
    </div>
  );
}

function ClusterCard({ cluster }: { cluster: Cluster }) {
  const aliveCount = cluster.servers.filter((s) => s.reachable && s.active).length;
  const totalCount = cluster.servers.length;
  return (
    <Card
      title={
        <span className="flex items-center gap-3">
          <span>{cluster.name}</span>
          <span className="text-zinc-500 font-mono text-xs normal-case">
            {cluster.domain}:{cluster.port}
          </span>
          {!cluster.is_active && <Pill tone="danger">выключен</Pill>}
        </span>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
            Серверов
          </div>
          <div className="text-3xl font-semibold">
            <span className={aliveCount === totalCount ? "text-emerald-300" : ""}>
              {aliveCount}
            </span>
            <span className="text-zinc-500">/{totalCount}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">живых / всего</div>
        </div>
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
            Коннекты
          </div>
          <div className="text-3xl font-semibold text-emerald-300">
            {cluster.total_connections.toLocaleString("ru-RU")}
          </div>
          <div className="text-xs text-zinc-500 mt-1">суммарно по кластеру</div>
        </div>
        <div className="md:text-right">
          <Link
            to={`/clusters/${cluster.id}`}
            className="inline-flex items-center h-8 px-3 text-xs font-medium rounded-md border bg-brand/15 text-brand border-brand/40 hover:bg-brand hover:text-brand-fg hover:border-brand transition-colors"
          >
            Открыть →
          </Link>
        </div>
      </div>
    </Card>
  );
}

function ClusterCreateForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("proxy.nolags.pw");
  const [port, setPort] = useState("443");
  const [adTag, setAdTag] = useState("");
  const [autoSecret, setAutoSecret] = useState(true);
  const [secret, setSecret] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api<Cluster>("/api/admin/clusters", {
        method: "POST",
        body: JSON.stringify({
          name,
          domain,
          port: Number(port) || 443,
          ad_tag: adTag || null,
          public_secret: autoSecret ? null : secret,
        }),
      }),
    onSuccess: () => onCreated(),
  });

  const invalid = !name.trim() || !domain.trim();

  return (
    <div className="space-y-4">
      <Field label="имя">
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="домен">
        <Input
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="proxy.nolags.pw"
        />
      </Field>
      <Field label="port">
        <Input value={port} onChange={(e) => setPort(e.target.value)} />
      </Field>
      <Field label="ad_tag">
        <Input
          value={adTag}
          onChange={(e) => setAdTag(e.target.value)}
          placeholder="необязательно, выдаётся @MTProxybot"
        />
      </Field>
      <div>
        <Checkbox
          checked={autoSecret}
          onChange={setAutoSecret}
          label="сгенерировать public_secret автоматически"
        />
      </div>
      {!autoSecret && (
        <Field label="public_secret">
          <Input
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="32 hex символа"
            className="font-mono"
          />
        </Field>
      )}
      {create.isError && (
        <div className="text-xs text-red-400">{(create.error as Error).message}</div>
      )}
      <div className="flex gap-3 pt-2 justify-end">
        <Button variant="ghost" onClick={onCancel} disabled={create.isPending}>
          Отмена
        </Button>
        <Button
          variant="primary"
          disabled={create.isPending || invalid}
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Создаём…" : "Создать"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
